import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { FC } from 'react';
import FieldWrapper from './wrappers/field-wrapper';

/**
 * Determines the border color based on error state and selection status.
 * @param {boolean | undefined} error - field fails validation
 * @param {boolean} selected - field is selected
 * @returns {string} - The CSS borderColor value (defaults to black)
 */
const getBorderColor = (error: boolean | undefined, selected: boolean): string => {
	if (error) return 'error.main'; // should prioritise error state
	if (selected) return 'secondary.main';
	return 'common.black';
};

type Layout = 'vertical' | 'horizontal';

interface IProps {
	id: string;
	label: string;
	description?: string | React.JSX.Element;
	options: { label: string; value: string }[];
	value?: unknown;
	onChange: (value: string) => void;
	hasError?: boolean;
	helperText?: string | false;
	layout?: Layout;
}

/**
 * Props interface for the RadioListField component.
 * @interface IProps
 * @property {string} [id] - id attribute for the component - useful for accessibility when you pass a description
 * @property {string} [label] - Label text for the radio group
 * @property {string | React.JSX.Element} [description] - Optional description text displayed below the label - use of FieldWrapper ensures this is mapped to the id for accessibility
 * @property {Array<{label: string, value: string}>} options - Array of options to display as radio buttons
 * @property {unknown} [value] - Currently selected value
 * @property {(value: string) => void} onChange - Handler function called when radio selection changes
 * @property {boolean} [hasError] - Whether the field has an error state
 * @property {string | false} [helperText] - Optional text to display as helper or error message
 * @property {Layout} [layout] - Layout direction: 'vertical' (stacked) or 'horizontal' (side-by-side). Defaults to 'vertical'
 */

/**
 * RadioListField component - Renders a styled list of radio button options with error handling.
 * The buttons can be arranged vertically (stacked) or horizontally (side-by-side) and have a clear border.
 * The border color changes depending on component state.
 *
 * @component
 * @param {IProps} props - Component props
 * @returns {JSX.Element} RadioListField component
 */
const RadioListField: FC<IProps> = ({
	id,
	label,
	description,
	options,
	value,
	onChange,
	hasError,
	helperText,
	layout = 'vertical',
}) => {
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange(event.target.value);
	};

	const isHorizontal = layout === 'horizontal';

	return (
		<FieldWrapper
			linkedInputId={id}
			label={label}
			description={description}
			hasError={hasError}
			helperText={helperText}
		>
			<RadioGroup
				id={id}
				aria-describedby={description ? `${id ?? ''}-description` : undefined}
				value={value ?? ''}
				onChange={handleChange}
				sx={{
					display: 'flex',
					flexDirection: isHorizontal ? 'row' : 'column',
					marginBottom: isHorizontal && hasError ? '8px' : 0,
				}}
			>
				{options.map((option) => {
					const selected = value === option.value;
					return (
						<Box
							key={option.value}
							sx={{
								border: 'solid',
								borderWidth: selected || hasError ? '2px' : '1.5px',
								borderColor: getBorderColor(hasError, selected),
								borderRadius: '4px',
								mb: isHorizontal ? 0 : '16px',
								mr: isHorizontal ? '16px' : 0,
								flex: isHorizontal ? '1 1 0' : 'none',
								transition: 'border-color 0.2s',
								'&:last-child': {
									mb: 0,
									mr: 0,
								},
							}}
						>
							<FormControlLabel
								sx={{ width: '100%', p: '6px 1rem 6px 7px', m: 0 }}
								control={<Radio checkedIcon={<CheckCircleIcon sx={{ color: 'secondary.main' }} />} />}
								label={option.label}
								value={option.value}
							/>
						</Box>
					);
				})}
			</RadioGroup>
		</FieldWrapper>
	);
};

export default RadioListField;
